import { useTranslation } from 'react-i18next';
import type { IWorkersProductivityRow } from '@interfaces/IProductivity';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { HardHatIcon } from '@utils/icons';
import { formatNumber } from '@utils/formatNumber';
import { CardHeader } from '../../../../shared/CardHeader/CardHeader';
import { ProductivityBar } from '../../Table/ProductivityBar/ProductivityBar';
import { makeSortableTh } from '../../Table/Th/makeSortableTh';
import { NameCell } from '../../Table/NameCell';
import { BreakdownTableFooter } from '../../Table/Footer';
import { useSortableRows } from '../../Table/useSortableRows';
import { usePagination } from '../../Table/usePagination';
import table from '../../Table/breakdownTable.module.css';
import styles from './WorkersPanel.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface IWorkersPanelProps {
  rows: IWorkersProductivityRow[];
}

export function WorkersPanel({ rows }: IWorkersPanelProps) {
  const { t } = useTranslation();
  const sort = useSortableRows(rows, { key: 'worker', dir: 'asc' });
  const pager = usePagination(sort.sorted, sort.sortSignature);
  const th = makeSortableTh('workers', sort, t);

  return (
    <>
      <CardHeader
        icon={<HardHatIcon size={16} />}
        title={t('productivity.workers.title')}
        meta={t('productivity.workers.meta')}
      />

      <p className={styles.note}>{t('productivity.workers.note')}</p>

      <ScrollArea orientation="horizontal">
        <table className={table.table}>
          <colgroup>
            <col className={styles.colWorker} />
            <col className={styles.colProfession} />
            <col className={styles.colProductivity} />
            <col className={styles.colActual} />
          </colgroup>
          <thead>
            <tr>
              {th('worker', 'productivity.workers.columns.worker')}
              <th className={table.th}>{t('productivity.workers.columns.profession')}</th>
              {th('productivity', 'productivity.workers.columns.productivity')}
              {th('actualWorkDays', 'productivity.workers.columns.actualWorkDays')}
            </tr>
          </thead>
          <tbody>
            {pager.visible.map((row) => (
              <tr key={row.id} className={table.tr}>
                <NameCell value={row.worker} />
                <td className={table.td}>
                  {row.profession ? (
                    <span className={table.teamText}>
                      <ProfessionPill profession={row.profession} compact truncate />
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className={table.td}>
                  <ProductivityBar value={row.productivity} />
                </td>
                <td className={table.td}>{formatNumber(row.actualWorkDays)}</td>
              </tr>
            ))}
          </tbody>
          <BreakdownTableFooter pager={pager} />
        </table>
      </ScrollArea>
    </>
  );
}
